import { useEffect, useRef, useState } from 'react';
import { frameCache } from './cache';
import type { DecodedFrame } from './FrameCache';

export interface FrameLoaderState {
  data: DecodedFrame | null;
  loading: boolean;
  error: string | null;
}

/**
 * Loads the frame at `index` through the FrameCache.
 *
 * - Cache hit → `getFrame` returns it synchronously; we paint immediately and
 *   just warm the prefetch window (no loading flash).
 * - Cache miss → keep the previous frame on screen while `loadFrame` fetches;
 *   a "latest request wins" ref discards out-of-order results.
 *
 * The cache owns fetch lifecycle + in-flight dedup, so this hook no longer
 * needs its own AbortController.
 */
export function useFrameLoader(index: number): FrameLoaderState {
  const [data, setData] = useState<DecodedFrame | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const latest = useRef(index);

  useEffect(() => {
    latest.current = index;

    const hit = frameCache.getFrame(index);
    if (hit) {
      setData(hit);
      setError(null);
      setLoading(false);
      frameCache.prefetch(index);
      return;
    }

    setLoading(true);
    frameCache
      .loadFrame(index)
      .then((d) => {
        if (latest.current !== index) return; // superseded
        setData(d);
        setError(null);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if ((e as Error).name === 'AbortError') return;
        if (latest.current !== index) return;
        setError(String(e));
        setLoading(false);
      });
  }, [index]);

  return { data, loading, error };
}
